/**
 * The batch's status at a glance: a stacked bar of the proportions and a chip per non-zero status
 * with its count. One click on a chip filters the job table below to that status; the active chip
 * is pressed.
 */
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { JOB_STATUSES, type JobStatus } from './importApi';
import { type StatusCountMap, totalCount } from './batchSummary';
import { JOB_STATUS_DISPLAY, statusTone } from './jobStatusChip';

interface StatusSummaryProps {
  counts: StatusCountMap;
  /** The status the job table is filtered to, or '' for all. */
  active: string;
  onSelect: (status: string) => void;
}

const StatusSummary: React.FC<StatusSummaryProps> = ({ counts, active, onSelect }) => {
  const total = totalCount(counts);
  const present = JOB_STATUSES.filter((s) => counts[s] > 0);

  return (
    <Box className="guide-import-summary">
      <Box
        className="guide-import-summary__bar"
        role="img"
        aria-label={`${total} files: ${present
          .map((s) => `${counts[s]} ${JOB_STATUS_DISPLAY[s].label.toLowerCase()}`)
          .join(', ')}`}
      >
        {total === 0 ? (
          <Box className="guide-import-summary__seg is-empty" />
        ) : (
          present.map((s) => (
            <Tooltip key={s} title={`${JOB_STATUS_DISPLAY[s].label}: ${counts[s]}`}>
              <Box
                className={`guide-import-summary__seg tone-${statusTone(s)}`}
                style={{ '--seg-pct': `${(counts[s] / total) * 100}%` } as React.CSSProperties}
              />
            </Tooltip>
          ))
        )}
      </Box>

      <Box className="guide-import-summary__chips" role="group" aria-label="Filter files by status">
        <Chip
          clickable
          aria-pressed={active === ''}
          variant={active === '' ? 'filled' : 'outlined'}
          className={`guide-import-summary__chip${active === '' ? ' is-pressed' : ''}`}
          label={`All ${total}`}
          onClick={() => onSelect('')}
        />
        {present.map((s: JobStatus) => {
          const on = active === s;
          return (
            <Chip
              key={s}
              data-status={s}
              clickable
              aria-pressed={on}
              variant={on ? 'filled' : 'outlined'}
              className={`guide-import-summary__chip tone-${statusTone(s)}${on ? ' is-pressed' : ''}`}
              label={
                <>
                  <Typography component="span" className="guide-import-summary__chip-label">
                    {JOB_STATUS_DISPLAY[s].label}
                  </Typography>{' '}
                  {counts[s]}
                </>
              }
              onClick={() => onSelect(s)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default StatusSummary;
